import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toApiFailure } from "../../lib/errors";
import { Button } from "../../components/ui/button";

/**
 * Shared trigger for the two Muss exports (F-012): CSV on the list (M-002)
 * and PDF on the detail (M-005). Secondary button with a Download icon; while
 * the export runs it is disabled and shows a spinner + "Wird erstellt ...".
 * Success/failure surface as toasts - failures prefer the server's German
 * RFC-7807 `detail` (NF-021) over the generic fallback.
 */
export function ExportButton({
  testid,
  label,
  successMessage,
  download,
}: {
  /** The `data-testid`; the literal sits at the call site (two screens). */
  testid: string;
  /** Resolved button label ("CSV-Export" / "Als PDF exportieren"). */
  label: string;
  /** Resolved success-toast text. */
  successMessage: string;
  /** Kicks off the actual download; rejects with an api-client failure. */
  download: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [isRunning, setIsRunning] = useState(false);

  const onClick = async (): Promise<void> => {
    setIsRunning(true);
    try {
      await download();
      toast.success(successMessage);
    } catch (err) {
      const failure = toApiFailure(err);
      toast.error(failure.problem?.detail ?? t("export.error"));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Button
      data-testid={testid}
      variant="secondary"
      disabled={isRunning}
      onClick={() => void onClick()}
    >
      {isRunning ? (
        <Loader2 aria-hidden="true" className="animate-spin" />
      ) : (
        <Download aria-hidden="true" />
      )}
      {isRunning ? t("export.running") : label}
    </Button>
  );
}
